import { keyBy, snakeCase } from 'lodash/fp';
import { useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ClickableDetail from 'dashboard/components/ClickableDetail';
import FieldAttributeItem from 'dashboard/components/FieldAttributeItem';
import TransactionStatusSticker from 'dashboard/components/TransactionStatusSticker';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const LinkRow = styled('div')`
  grid-column-end: span 3;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${FontWeight.Regular};
`;

export interface ResourceTransactionProps {
  transaction: Transaction;
  transactionStatus?: TransactionStatus;
  transactionType?: TransactionType;
  hideEmptyAttributes?: boolean;
  defaultAccordionOpen: boolean;
}

const ResourceTransaction = ({
  transaction,
  transactionStatus,
  transactionType,
  hideEmptyAttributes = false,
  defaultAccordionOpen,
}: ResourceTransactionProps) => {
  // Snake case because we inflect field keys for `fields` but not for `field-schemas`.
  // Ideally we don't inflect field keys, ever.
  const fieldSchemaMap = useMemo(
    () => keyBy(({ key }) => snakeCase(key), transactionType?.attributes['field-schemas'] ?? []),
    [transactionType],
  );

  if (transactionType == null) {
    return (
      <ClickableDetail
        header={
          <HeaderContainer>
            <Header>
              <Heading size='sm'>Transaction - {transaction.id}</Heading>
              <Spacer width={1} />
            </Header>
            <LinkRow>
              <StyledLink to={`/dashboard/transactions/${transaction.id}`}>
                {transaction.id}
              </StyledLink>
            </LinkRow>
          </HeaderContainer>
        }
        isDefaultOpen={defaultAccordionOpen}
      >
        <Card>
          <Card.Body>
            <Paragraph>No transaction type found for this transaction.</Paragraph>
          </Card.Body>
        </Card>
      </ClickableDetail>
    );
  }

  const isRedacted = transaction.attributes['redacted-at'] != null;

  const fields = transaction.attributes.fields ?? {};
  const fieldNamesToShow = Object.keys(fields)
    .filter((fieldName) => fieldSchemaMap[snakeCase(fieldName)] != null)
    .filter((fieldName) => fields[fieldName] != null);

  const fieldsAttribute = fieldNamesToShow.map((fieldName: string) => {
    const fieldValue = transaction.attributes.fields[fieldName];
    const fieldSchema = fieldSchemaMap[snakeCase(fieldName)];

    return (
      <FieldAttributeItem
        key={fieldSchema.key}
        field={fieldValue}
        fieldSchema={fieldSchema}
        isRedacted={isRedacted}
        hideEmptyAttributes={hideEmptyAttributes}
        wordBreak='break-all'
      />
    );
  });

  return (
    <ClickableDetail
      header={
        <HeaderContainer>
          <Header>
            <Heading size='sm'>Transaction - {transactionType.attributes.name}</Heading>
            <Spacer width={1} />
            {transactionStatus && (
              <TransactionStatusSticker transactionStatus={transactionStatus} />
            )}
          </Header>
          <LinkRow>
            <StyledLink to={`/dashboard/transactions/${transaction.id}`}>
              {transaction.id}
            </StyledLink>
          </LinkRow>
        </HeaderContainer>
      }
      isDefaultOpen={defaultAccordionOpen}
    >
      <Card>
        <Card.Body>
          <Card.Title>Fields</Card.Title>

          <AttributeGrid colCount={3}>{fieldsAttribute}</AttributeGrid>
        </Card.Body>
      </Card>
    </ClickableDetail>
  );
};

export default ResourceTransaction;
